import React from 'react';
import './MobileGameHeader.css';

// Mobile-only top strip for the game screen.
// Layout:
//   ┌──────────────┬──────────────┐
//   │ White        │ Black        │
//   │ Turn 0:35    │ Turn 0:41    │
//   │ Total 12:34  │ Total 10:22  │
//   ├──────────────┴──────────────┤
//   │ Captured: D A S C R M N P   │  descending point value
//   │           d a c r c n p     │  (b = took from white, w = took from black)
//   └─────────────────────────────┘
//
// Times fall back to em-dash when the timer is unlimited or missing.
// Captured pieces render in the fixed descending-value order the user
// spec'd (Donkey > Advisor = Spy = Cannon > Ram > Camel = Knight > Pawn).

const CAPTURED_ORDER = [
  'donkey',   // 6
  'advisor',  // 5
  'spy',      // 5
  'cannon',   // 5
  'ram',      // 4
  'camel',    // 3
  'knight',   // 3
  'pawn',     // 1
];

function formatTime(seconds) {
  if (seconds == null || seconds === Infinity) return '—';
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

function CapturedRow({ pieces, colorAttacker }) {
  // pieces: array of captured piece objects (each has .type, .color).
  // The .color on a captured piece is the color that was TAKEN, so the
  // attacker is the OPPOSITE color. Group by type in the fixed order.
  const byType = {};
  for (const p of pieces) {
    byType[p.type] = (byType[p.type] || 0) + 1;
  }

  const items = CAPTURED_ORDER
    .filter((t) => byType[t] > 0)
    .map((t) => ({ type: t, count: byType[t] }));

  if (items.length === 0) {
    return <div className="captured-row empty" aria-label="No captures yet">·</div>;
  }

  return (
    <div className="captured-row" aria-label={`Pieces captured by ${colorAttacker}`}>
      {items.map(({ type, count }) => (
        <span key={type} className="captured-item" title={type}>
          <img
            src={`/assets/pieces/${colorAttacker === 'white' ? 'black' : 'white'}-${type}.png`}
            alt={type}
            className="captured-icon"
          />
          {count > 1 && <span className="captured-count">×{count}</span>}
        </span>
      ))}
    </div>
  );
}

export default function MobileGameHeader({
  gameState,
  timeLeft,       // { white: seconds, black: seconds } total time
  turnTimeLeft,   // { white: seconds, black: seconds } per-turn time
}) {
  const captured = gameState?.capturedPieces || { white: [], black: [] };

  return (
    <div className="mobile-game-header" role="group" aria-label="Game status">
      <div className="mgh-clocks">
        <div className="mgh-clock mgh-clock-white">
          <div className="mgh-clock-name">White</div>
          <div className="mgh-clock-turn">
            <span className="mgh-clock-label">Turn</span>
            <span className="mgh-clock-value">{formatTime(turnTimeLeft?.white)}</span>
          </div>
          <div className="mgh-clock-total">
            <span className="mgh-clock-label">Total</span>
            <span className="mgh-clock-value">{formatTime(timeLeft?.white)}</span>
          </div>
        </div>
        <div className="mgh-clock mgh-clock-black">
          <div className="mgh-clock-name">Black</div>
          <div className="mgh-clock-turn">
            <span className="mgh-clock-label">Turn</span>
            <span className="mgh-clock-value">{formatTime(turnTimeLeft?.black)}</span>
          </div>
          <div className="mgh-clock-total">
            <span className="mgh-clock-label">Total</span>
            <span className="mgh-clock-value">{formatTime(timeLeft?.black)}</span>
          </div>
        </div>
      </div>

      <div className="mgh-captured">
        {/* Row of pieces White took (i.e. captured black pieces on the board). */}
        <CapturedRow pieces={captured.black || []} colorAttacker="white" />
        {/* Row of pieces Black took (captured white pieces). */}
        <CapturedRow pieces={captured.white || []} colorAttacker="black" />
      </div>
    </div>
  );
}
